import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {userSelector} from '../../stores/selectors/appStateSelector';
import {modalCreateUpdateEmployeeStateSelector} from '../../stores/selectors/manageEmployeeStateSelector';
import MessageApi from '../../api/rest/dropboxMessageApi';
import {setAlert} from '../../stores/actions/alertModalStateAction';
import {FmlxButton, FmlxIcon, FmlxTab} from 'fmlx-common-ui';
import {useConfirmationModal} from '../../hooks/useConfirmation';
import {ALERT_VARIANT, EPathname, ESelectedTabs, ResponseCodeStatus} from '../../const/dataEnum';
import MessageContent from '../DropboxMessage/MessageContent';
import MessageCreatemodal from '../DropboxMessage/MessageModal/MessageCreatorModal';
import ConfirmationModal from '../DropboxMessage/MessageModal/MessageConfirmationModal';
import './MessageInbox.scss';

const MessageInbox = () => {
  const user = useSelector(userSelector);
  const {userAuthRoleId, userDeptId} = useSelector(modalCreateUpdateEmployeeStateSelector);
  const [initialMessage, setInitialMessage] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [savedPayload, setSavedPayload] = useState({});
  const [messageTeamData, setMessageTeamData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = user?.id;
  const location = useLocation();
  const pathname = location.pathname;
  const dispatch = useDispatch();

  const {confirmOpen, confirmationMessage, setConfirmationMessage, setConfirmOpen} = useConfirmationModal();

  const fetchMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      if (pathname.includes('/Team')) {
        const response = await MessageApi.fetchMessageBySender(userId);
        setMessageTeamData(response.data || []);
      } else {
        const response = await MessageApi.fetchALLMessage();
        setInitialMessage(response.data || []);
      }
    } catch (err) {
      setError(err);
      console.error('Error fetching messages:', err);
    } finally {
      setIsLoading(false);
    }
  }, [pathname, selectedTab, userId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const memoizedItems = useMemo(() => {
    if (pathname.includes(EPathname.MESSAGE_INBOX_USER)) {
      return [
        {
          title: ESelectedTabs.SENT_MESSAGE,
          content: (
            <MessageContent
              selectedTab={1}
              userAuthRoleID={userAuthRoleId}
              message={messageTeamData}
              userProfile={userId}
              onDataRefresh={fetchMessages}
            />
          ),
        },
      ];
    } else {
      return [
        {
          title: ESelectedTabs.ALL_MESSAGE,
          content: (
            <MessageContent
              selectedTab={0}
              userRole={userAuthRoleId}
              message={initialMessage}
              isLoading={isLoading}
              error={error}
              onDataRefresh={fetchMessages}
            />
          ),
        },
      ];
    }
  }, [pathname, selectedTab, initialMessage, messageTeamData, isLoading, error, userAuthRoleId]);

  const handleChange = ({index}) => {
    setSelectedTab(index);
  };

  const handleOnCancelConfirmation = () => {
    setConfirmOpen(false);
  };

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleOnSend = async (payload) => {
    setSavedPayload(payload);
    const response = await MessageApi.createMessage(payload);
    if (
      response.message.includes(
        'A message with the same type already exists in the current review period for the specified receiver.'
      )
    ) {
      setConfirmOpen(true);
      setConfirmationMessage(response.message);
    } else if (
      response.responseCode === ResponseCodeStatus.SUCCESS ||
      response.message.includes('A new message created.')
    ) {
      dispatch(
        setAlert({
          show: true,
          variant: ALERT_VARIANT.SUCCESS,
          message: response.message,
        })
      );
      fetchMessages();
    }
    handleCloseModal();
  };

  return (
    <div className="dropbox-container">
      <div className="message-inbox-header">
        <FmlxButton
          className="send-message--button"
          label="Send Message"
          size="lg"
          type="primary"
          variant="contain"
          fullWidth="fullwidth"
          onClick={handleOpenModal}
          withIcon={FmlxButton.WithIcon.START}
          icon={<FmlxIcon name={'SendACopy'} />}
        />
      </div>
      <FmlxTab
        id="fmlx-tab-horizontal"
        items={memoizedItems}
        selectedTab={selectedTab}
        variant={FmlxTab.Variant.FULLWIDTH}
        orientation={FmlxTab.Orientation.HORIZONTAL}
        onChange={handleChange}
      />
      {open && (
        <>
          <MessageCreatemodal open={open} userData={userDeptId} onSend={handleOnSend} onClose={handleCloseModal} />
        </>
      )}
      {confirmOpen && (
        <>
          <ConfirmationModal
            open={confirmOpen}
            onSend={handleOnSend}
            onClose={handleOnCancelConfirmation}
            savedPayload={savedPayload}
            confirmationMessage={confirmationMessage}
          />
        </>
      )}
    </div>
  );
};

export default MessageInbox;
